import type { Identifier } from "ra-core";

import type {
  FinanceCharge,
  FinanceContract,
  FinanceExpense,
} from "../../types";
import {
  addToMonth,
  buildChargesForMonth,
  computeMrr,
  getChargeStatus,
  getDaysOverdue,
  getReceivedAmount,
  isContractBilling,
  monthRange,
  roundMoney,
  toDateOnly,
  toMonth,
} from "./financeSchedule";

export interface FinanceMonthPoint {
  /** `YYYY-MM-01` */
  month: string;
  mrr: number;
  /** Non-cancelled charges of the month (competence) */
  billed: number;
  /** Payments received during the month (cash) */
  received: number;
  setupReceived: number;
  recurringReceived: number;
  /** Expenses of the month (competence) */
  expensesDue: number;
  /** Expenses paid during the month (cash) */
  expensesPaid: number;
  /** received - expensesPaid */
  net: number;
}

export type FinanceAgingBucket = "1_15" | "16_30" | "31_60" | "61_plus";

export interface FinanceAgingPoint {
  bucket: FinanceAgingBucket;
  amount: number;
  count: number;
}

export interface FinanceCategoryAmount {
  category: string;
  amount: number;
}

export interface FinanceCompanyShare {
  company_id: Identifier;
  mrr: number;
  /** Share of the total MRR, 0..1 */
  share: number;
}

export interface FinanceForecastPoint {
  month: string;
  recurring: number;
  setup: number;
  oneOff: number;
}

export interface FinanceKpis {
  mrr: number;
  mrrPrevious: number;
  activeContracts: number;
  activeClients: number;
  /** Average MRR per active client */
  arpa: number;
  billedThisMonth: number;
  receivedThisMonth: number;
  overdueAmount: number;
  overdueCount: number;
  /** Overdue amount / amount already due, 0..1 */
  delinquencyRate: number;
  expensesThisMonth: number;
  expensesPaidThisMonth: number;
  netThisMonth: number;
  /** netThisMonth / receivedThisMonth, null without revenue */
  margin: number | null;
  /** Setup not received yet, generated or not */
  setupToReceive: number;
  /** Share of the MRR of the biggest client, 0..1 */
  topClientShare: number;
}

export interface FinanceMetrics {
  kpis: FinanceKpis;
  monthly: FinanceMonthPoint[];
  aging: FinanceAgingPoint[];
  expensesByCategory: FinanceCategoryAmount[];
  concentration: FinanceCompanyShare[];
  forecast: FinanceForecastPoint[];
}

export interface FinanceMetricsInput {
  contracts: FinanceContract[];
  charges: FinanceCharge[];
  expenses: FinanceExpense[];
  today: Date;
  /** Months of history, the current one included */
  months?: number;
  forecastMonths?: number;
}

const AGING_BUCKETS: { bucket: FinanceAgingBucket; maxDays: number }[] = [
  { bucket: "1_15", maxDays: 15 },
  { bucket: "16_30", maxDays: 30 },
  { bucket: "31_60", maxDays: 60 },
  { bucket: "61_plus", maxDays: Number.POSITIVE_INFINITY },
];

const TOP_CLIENTS = 10;

const sum = <T>(items: T[], amount: (item: T) => number): number =>
  roundMoney(items.reduce((total, item) => total + amount(item), 0));

const isLive = (charge: FinanceCharge) => !charge.cancelled_at;

const paidInMonth = (paidAt: string | null | undefined, month: string) =>
  paidAt != null && toMonth(paidAt) === month;

const buildMonthPoint = (
  month: string,
  { contracts, charges, expenses }: FinanceMetricsInput,
): FinanceMonthPoint => {
  const receivedCharges = charges.filter(
    (charge) => isLive(charge) && paidInMonth(charge.paid_at, month),
  );
  const received = sum(receivedCharges, getReceivedAmount);
  const setupReceived = sum(
    receivedCharges.filter((charge) => charge.kind === "setup"),
    getReceivedAmount,
  );
  const expensesPaid = sum(
    expenses.filter((expense) => paidInMonth(expense.paid_at, month)),
    (expense) => expense.amount,
  );
  return {
    month,
    mrr: computeMrr(contracts, month),
    billed: sum(
      charges.filter(
        (charge) => isLive(charge) && charge.competence_month === month,
      ),
      (charge) => charge.amount,
    ),
    received,
    setupReceived,
    recurringReceived: roundMoney(received - setupReceived),
    expensesDue: sum(
      expenses.filter((expense) => expense.competence_month === month),
      (expense) => expense.amount,
    ),
    expensesPaid,
    net: roundMoney(received - expensesPaid),
  };
};

/** Overdue receivables grouped by how late they are */
export const computeAging = (
  charges: FinanceCharge[],
  today: Date,
): FinanceAgingPoint[] => {
  const overdue = charges.map((charge) => ({
    charge,
    days: getDaysOverdue(charge, today),
  }));
  return AGING_BUCKETS.map(({ bucket, maxDays }, index) => {
    const minDays = index === 0 ? 1 : AGING_BUCKETS[index - 1].maxDays + 1;
    const inBucket = overdue.filter(
      ({ days }) => days >= minDays && days <= maxDays,
    );
    return {
      bucket,
      amount: sum(inBucket, ({ charge }) => charge.amount),
      count: inBucket.length,
    };
  });
};

/** MRR per company, biggest first, limited to the top clients */
export const computeConcentration = (
  contracts: FinanceContract[],
  month: string,
): FinanceCompanyShare[] => {
  const total = computeMrr(contracts, month);
  const byCompany = new Map<Identifier, number>();
  contracts
    .filter((contract) => isContractBilling(contract, month))
    .forEach((contract) =>
      byCompany.set(
        contract.company_id,
        (byCompany.get(contract.company_id) ?? 0) + contract.monthly_amount,
      ),
    );
  return [...byCompany.entries()]
    .map(([company_id, mrr]) => ({
      company_id,
      mrr: roundMoney(mrr),
      share: total > 0 ? mrr / total : 0,
    }))
    .filter(({ mrr }) => mrr > 0)
    .sort((a, b) => b.mrr - a.mrr)
    .slice(0, TOP_CLIENTS);
};

/**
 * Revenue expected in the coming months: the open charges already generated
 * plus the charges the contracts will generate (same rule as the "generate the
 * month" action, so a month is never counted twice).
 */
export const computeForecast = (
  contracts: FinanceContract[],
  charges: FinanceCharge[],
  firstMonth: string,
  count: number,
): FinanceForecastPoint[] =>
  monthRange(firstMonth, count).map((month) => {
    const open = charges.filter(
      (charge) =>
        isLive(charge) && !charge.paid_at && charge.competence_month === month,
    );
    const projected = buildChargesForMonth(contracts, charges, month);
    const all = [...open, ...projected];
    const byKind = (kind: FinanceCharge["kind"]) =>
      sum(
        all.filter((charge) => charge.kind === kind),
        (charge) => charge.amount,
      );
    return {
      month,
      recurring: byKind("recurring"),
      setup: byKind("setup"),
      oneOff: byKind("one_off"),
    };
  });

const computeSetupToReceive = (
  contracts: FinanceContract[],
  charges: FinanceCharge[],
): number =>
  sum(
    contracts.filter((contract) => contract.status !== "cancelled"),
    (contract) => {
      const received = sum(
        charges.filter(
          (charge) =>
            isLive(charge) &&
            charge.paid_at &&
            charge.kind === "setup" &&
            charge.contract_id === contract.id,
        ),
        getReceivedAmount,
      );
      return Math.max(contract.setup_amount - received, 0);
    },
  );

/** Every number and series of the finance overview, from the raw records */
export const computeFinanceMetrics = (
  input: FinanceMetricsInput,
): FinanceMetrics => {
  const {
    contracts,
    charges,
    expenses,
    today,
    months = 12,
    forecastMonths = 6,
  } = input;
  const currentMonth = toMonth(today);
  const monthly = monthRange(addToMonth(currentMonth, 1 - months), months).map(
    (month) => buildMonthPoint(month, input),
  );
  const current = monthly[monthly.length - 1];
  const previous = monthly[monthly.length - 2];

  const overdue = charges.filter(
    (charge) => getChargeStatus(charge, today) === "overdue",
  );
  const overdueAmount = sum(overdue, (charge) => charge.amount);
  // Base of the delinquency rate: live charges whose due date has passed
  const todayDate = toDateOnly(today);
  const dueAmount = sum(
    charges.filter((charge) => isLive(charge) && charge.due_date < todayDate),
    (charge) => charge.amount,
  );

  const billingContracts = contracts.filter((contract) =>
    isContractBilling(contract, currentMonth),
  );
  const activeClients = new Set(
    billingContracts
      .filter((contract) => contract.monthly_amount > 0)
      .map((contract) => contract.company_id),
  ).size;
  const concentration = computeConcentration(contracts, currentMonth);
  const windowMonths = new Set(monthly.map(({ month }) => month));
  const byCategory = new Map<string, number>();
  expenses
    .filter((expense) => windowMonths.has(expense.competence_month))
    .forEach((expense) =>
      byCategory.set(
        expense.category,
        (byCategory.get(expense.category) ?? 0) + expense.amount,
      ),
    );

  return {
    kpis: {
      mrr: current.mrr,
      mrrPrevious: previous?.mrr ?? 0,
      activeContracts: billingContracts.length,
      activeClients,
      arpa: activeClients > 0 ? roundMoney(current.mrr / activeClients) : 0,
      billedThisMonth: current.billed,
      receivedThisMonth: current.received,
      overdueAmount,
      overdueCount: overdue.length,
      delinquencyRate: dueAmount > 0 ? overdueAmount / dueAmount : 0,
      expensesThisMonth: current.expensesDue,
      expensesPaidThisMonth: current.expensesPaid,
      netThisMonth: current.net,
      margin: current.received > 0 ? current.net / current.received : null,
      setupToReceive: computeSetupToReceive(contracts, charges),
      topClientShare: concentration[0]?.share ?? 0,
    },
    monthly,
    aging: computeAging(charges, today),
    expensesByCategory: [...byCategory.entries()]
      .map(([category, amount]) => ({ category, amount: roundMoney(amount) }))
      .sort((a, b) => b.amount - a.amount),
    concentration,
    forecast: computeForecast(
      contracts,
      charges,
      addToMonth(currentMonth, 1),
      forecastMonths,
    ),
  };
};
